import * as React from 'react';
import {
  styled,
  Box,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUploadContext from '../../../../../../../hooks/useUploadContext/useUploadContext';
import { MediaTypes } from '../../../../../../../commands/practitioner/enums/MediaTypes';
import { FileUploadStyle } from './styles';
import ButtonUI from '../../../../../../UI/Button/Button';
import heic2any from 'heic2any';
import useAppContext from '../../../../../../../hooks/useAppContext/useAppContext';
import { DropZoneUI } from '../../../../../../UI/DropZone/DropZoneUI';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
  color: 'blue',
});

interface FileUploadProps {
  onFileUpload: Function;
  onTypeSelect: Function;
  index: number;
  value: number;
}

const FileUpload = (props: FileUploadProps) => {
  const { t } = useTranslation();
  const { onFileUpload, onTypeSelect, index, value } = props;
  const { hasDevice } = useAppContext();
  const { uploadedFileName, setUploadedFileName } = useUploadContext();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [tempFileName, setTempFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { fileUploadContainer } = FileUploadStyle;
  const maximumFileName = 25;
  //formats, that suitable for us
  const allowedImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
  const allowedMediaTypes = ['audio/mpeg', 'video/mp4', 'video/ogg', 'video/webm'];

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleSnackbarOpen = () => {
    setOpenSnackbar(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDialogOpen = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setOpenDialog(true);
  };

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    setTempFileName(inputValue);
    const intervalId = setInterval(() => {
      setUploadedFileName(inputValue);
    }, 500);

    setTimeout(() => {
      clearInterval(intervalId);
    }, 500);
  };

  const handleFileNameConfirm = () => {
    if (selectedFile) {
      setUploadedFileName(tempFileName);
      onFileUpload(selectedFile);
      setOpenDialog(false);
    }
  };

  const autoType = (selectedType: string) => {
    if (selectedType.startsWith('video/')) {
      return MediaTypes.VIDEO;
    } else if (selectedType.startsWith('image/')) {
      return MediaTypes.IMAGE;
    } else if (selectedType.startsWith('audio/')) {
      return MediaTypes.AUDIO;
    }
    return MediaTypes.VIDEO;
  };

  const fileNameLogic = useCallback(
    (file: File) => {
      const defaultFileName = file.name;
      const imageSize = file.size;
      const maximumFileSize = 32 * 1024 * 1024; // 32MB;

      if (imageSize > maximumFileSize) {
        setMessage(t('too-big-size'));
        handleSnackbarOpen();
        return;
      }

      setSelectedFile(file);
      setTempFileName(defaultFileName);
      setMessage('');
      handleDialogOpen();
    },
    [t]
  );

  const handleChange = useCallback(
    async (selectedFiles: File[] | []) => {
      if (selectedFiles.length === 0) return;
      if (selectedFiles.length > 1) {
        setMessage(t('to-much-uploaded-files'));
        handleSnackbarOpen();
        return;
      }

      let file = selectedFiles[0];

      // Automatically trim the file name to a maximum of 25 characters.
      let originalFileName = file.name;
      if (originalFileName.length > maximumFileName) {
        const trimmedFileName = originalFileName.slice(0, maximumFileName);
        file = new File([file], trimmedFileName, { type: file.type });
      }

      const uploadingType = file.type;
      const fileName = file.name.toLowerCase();

      let newType;
      let sendingFile = file;

      if (!uploadingType && fileName.includes('.heic')) {
        const blob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.8,
        });

        if (blob instanceof Blob) {
          sendingFile = new File([blob], file.name.replace('.heic', '.jpg'), {
            type: 'image/jpeg',
          });
          newType = MediaTypes.IMAGE;
        }
      } else if (allowedImageTypes.includes(uploadingType) || allowedMediaTypes.includes(uploadingType)) {
        newType = autoType(uploadingType);
        sendingFile = file;
      } else {
        setMessage(t(hasDevice ? 'upload-extension-plus' : 'upload-extension'));
        handleSnackbarOpen();
        return;
      }

      onTypeSelect(null, newType);
      fileNameLogic(sendingFile);
    },
    [allowedImageTypes, allowedMediaTypes, fileNameLogic, onTypeSelect, t, hasDevice]
  );

  return (
    <>
      {index === value && (
        <Box sx={fileUploadContainer}>
          <DropZoneUI onDrop={handleChange} desc={t('drop-zone')} />
          <Snackbar className="snack-bar" open={openSnackbar} autoHideDuration={4000} onClose={handleSnackbarClose}>
            <Alert className="alert" elevation={6} variant="filled" severity="info">
              {message}
            </Alert>
          </Snackbar>
          <Dialog className="dialog" open={openDialog} onClose={handleDialogClose} sx={FileUploadStyle.dialog}>
            <DialogTitle>{t('confirm-file-name')}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                type="text"
                fullWidth
                value={tempFileName}
                onChange={handleFileNameChange}
                className={!!uploadedFileName ? '' : 'empty'}
                inputProps={{ maxLength: maximumFileName }}
              />
            </DialogContent>
            <DialogActions>
              <ButtonUI
                className={'cancel-btn'}
                onClickHandler={handleDialogClose}
                text={t('cancel')}
                variant={'outlined'}
              />
              <ButtonUI
                className={'confirm-btn'}
                onClickHandler={handleFileNameConfirm}
                text={t('confirm')}
                variant="contained"
              />
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
};

export default FileUpload;
